import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AddIcon from "@mui/icons-material/Add";
import GroupOffIcon from "@mui/icons-material/GroupOff";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Tooltip from "@mui/material/Tooltip";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useRowChoiceRequiresCredentials } from "_shared/_views/tables/row-choice-requires-credentials";
import { useRowChoiceLabel } from "_shared/_views/tables/row-choice-label";
import Guid from "_shared/guids/guid";
import { Currency, formatCurrency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import { MarketplaceResultType } from "config";
import MarketplaceChatCardContent from "marketplace/_views/marketplace-chat-card-content";
import MarketplaceProfileLink, { getMarketplaceProfilePath } from "marketplace/_views/marketplace-profile-link";
import MarketplaceResultCard, {
  MarketplaceResultAvatar,
  MarketplaceResultFact,
  MarketplaceResultFactList,
} from "marketplace/_views/marketplace-result-card";
import MarketplaceResultFooter from "marketplace/_views/marketplace-result-footer";
import {
  getApplicationsText,
  getCityAndStateText,
  getWholeDollarText,
} from "marketplace/_views/result-text";
import IndividualBookmarkButton from "marketplace/bookmarks/_views/individual-bookmark-button";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { individualsApi, useGetIndividualAvatarQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualAvatar } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import MarketplaceIndividualInfoSchema, {
  MarketplaceIndividualInfoJSON,
} from "marketplace/_schema/marketplace-individual-info-schema";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import {
  areCredentialsFilledOut,
  credentialsMissingReason,
} from "marketplace/individuals/profiles/professional-credentials";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import Session from "users/__session/session";
import startDraftProposalWithVendorTeamLeader from "work/proposals/_views/drafts/start-draft-proposal-with-vendor-team-leader";

export enum TeamMemberType {
  Leader = "leader",
  Member = "member",
}

type IndividualResultProps = {
  selectingAsLeader?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  compactLayout?: boolean;
  result: MarketplaceIndividualInfoJSON;
  embedded: boolean;
  session: Session;
  navigate?: NavigateFunction;
  getText: (fieldNames: string[], base: unknown, noCommas?: boolean) => string;
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth?: boolean,
  ) => void;
  onTeamMemberSelected?: (teamMember: Individual) => void;
};

function getBillingRateDisplayValue(billingRate: string): string {
  const sanitizedBillingRate = billingRate.replace(/[$,\s]/g, "");

  if (!/^-?(?:\d+|\d*\.\d+)$/.test(sanitizedBillingRate)) {
    return billingRate;
  }

  const parsedBillingRate = Number.parseFloat(sanitizedBillingRate);

  if (!Number.isFinite(parsedBillingRate)) {
    return billingRate;
  }

  return formatCurrency(new Money(parsedBillingRate, Currency.USD));
}

export default function IndividualResult(props: Readonly<IndividualResultProps>) {
  const {
    selectingAsLeader,
    selectedTeamMemberUserIds,
    compactLayout,
    result,
    embedded,
    session,
    navigate,
    getText,
    handleResultClicked,
    onTeamMemberSelected,
  } = props;

  const { id } = result;
  const individualId = React.useMemo(() => (id ? new Guid(id) : null), [id]);

  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();
  const [isPublishing, setIsPublishing] = React.useState<boolean>(false);
  const [isPublished, setIsPublished] = React.useState<boolean>(false);
  const cannotChooseReasonId = React.useId();
  const individual = MarketplaceIndividualInfoSchema.deserialize(result);
  // Only the individual may publish their own profile. The marketplace search also returns
  // unpublished members of a legal entity the caller can read, so "this result reached me"
  // does not imply "I may publish it" - acting on someone else's profile is a 401.
  const isOwnProfile = Boolean(result.userId && session.user?.id?.isEqualTo(new Guid(result.userId)));
  const isUnpublished = result.isVisible === false && !isPublished;
  const avatarId = result.avatarId;
  const fullName = getText(["firstName", "lastName"], result, true);

  const dispatch = useAttorneyHubDispatch();
  // The picker that drew this result says what choosing it does: see `RowChoiceLabel`, and
  // `RowChoiceRequiresCredentials` for whether it seats the person on a vendor team.
  const choiceLabel = useRowChoiceLabel();
  const requiresCredentials = useRowChoiceRequiresCredentials();

  const avatar = useSelector(getIndividualAvatar(individualId, "medium"));
  useGetIndividualAvatarQuery(
    { individualId: individualId?.value ?? "", size: "medium" },
    { skip: !individualId || !!avatar || !avatarId },
  );

  async function contactIndividual() {
    try {
      if (!individualId) throw new Error("Individual ID is required to contact individual.");
      const forumResponse = await dispatch(
        individualsApi.endpoints.contactIndividual.initiate({ individualId: individualId.value }),
      ).unwrap();
      setForum(Forum.fromJSON(forumResponse));
      setShowChat(true);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't find user", { variant: "error" });
    }
  }

  function getAddButton(result: MarketplaceIndividualInfoJSON) {
    let tooltipTitle = "";
    let buttonIcon = <AddIcon />;
    // Choosing a leader says so; otherwise the picker that drew this result names the choice.
    let buttonLabel = selectingAsLeader ? "Select As Leader" : choiceLabel;

    const alreadyOnTeam = selectedTeamMemberUserIds?.some((id) => id.isEqualTo(new Guid(result.userId!)));
    const profileNotVisible = isUnpublished && isOwnProfile;
    const cannotLead = Boolean(selectingAsLeader && !result.hasSigningAuthority);
    // No team takes a member whose profile lists no professional credentials (#5800); a leader is
    // not held to it. Their own unpublished profile still offers to publish, which needs none.
    const lacksCredentials =
      requiresCredentials && !selectingAsLeader && !profileNotVisible && !areCredentialsFilledOut(result.credentials);
    const isDisabled = cannotLead || lacksCredentials || alreadyOnTeam || isPublishing;
    // A disabled button takes no keyboard focus, so a reason kept in a tooltip reaches only a mouse.
    // Say it as text under the button instead.
    let cannotChooseReason: string | undefined;
    if (!alreadyOnTeam && cannotLead) cannotChooseReason = "Can't lead a team without signing authority";
    if (!alreadyOnTeam && lacksCredentials) cannotChooseReason = credentialsMissingReason;

    if (profileNotVisible) {
      tooltipTitle = "Publish your profile to the marketplace to add to vendor team";
      buttonIcon = <AccountCircleIcon />;
      buttonLabel = "Publish Profile";
    }
    if (alreadyOnTeam) {
      tooltipTitle = "Already on vendor team";
      buttonIcon = <GroupOffIcon />;
      buttonLabel = "On Team";
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "inherit" }}>
        <Tooltip title={tooltipTitle}>
          <span style={{ display: "flex", flexDirection: "column" }}>
            <Button
              variant="outlined"
              color="primary"
              aria-label={buttonLabel}
              aria-describedby={cannotChooseReason ? cannotChooseReasonId : undefined}
              startIcon={buttonIcon}
              disableElevation
              disabled={isDisabled}
              onClick={() => handleSelectMemberButtonClicked(result)}
            >
              {buttonLabel}
            </Button>
          </span>
        </Tooltip>
        {cannotChooseReason && (
          <FormHelperText id={cannotChooseReasonId} sx={{ marginLeft: 0 }}>
            {cannotChooseReason}
          </FormHelperText>
        )}
      </Box>
    );
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  // The avatar repeats the name's link for the mouse. The name is the one keyboard stop, so the
  // avatar stays out of the tab order and out of the accessibility tree.
  function linkAvatarToProfile(avatarElements: React.ReactNode) {
    const profileId = individual.id.value;
    return (
      <MarketplaceProfileLink
        profileType={MarketplaceResultType.Individual}
        profileId={profileId}
        onOpen={(event) => openIndividualProfile(event, profileId)}
        tabIndex={-1}
        aria-hidden="true"
        underline="none"
        sx={{ display: "contents" }}
      >
        {avatarElements}
      </MarketplaceProfileLink>
    );
  }

  function handleSelectMemberButtonClicked(result: MarketplaceIndividualInfoJSON) {
    const individual = MarketplaceIndividualInfoSchema.deserialize(result);
    if (isUnpublished) {
      if (isOwnProfile) void publishProfile(individual);
      return;
    }
    onTeamMemberSelected?.(individual);
  }

  function canCreateProposalFromResult() {
    // Do not render button if result is self
    const resultUserId = result?.userId ? new Guid(result.userId) : undefined;
    if (!resultUserId || session.user?.id?.isEqualTo(resultUserId)) return false;

    const resultEntityId = result?.legalEntityId ? new Guid(result.legalEntityId) : undefined;
    if (!resultEntityId) return false;

    // Do not render button if user is same entity as target
    const userEntityId = session.user!.legalEntityId;
    return userEntityId?.isEqualTo(resultEntityId) === false;
  }

  // A new draft with the result as its vendor team leader. A client who wants several firms to bid
  // sends that draft to them from the draft (#6464).
  function startProposal() {
    if (!result.userId || !result.legalEntityId || !navigate) return;
    startDraftProposalWithVendorTeamLeader({
      dispatch,
      leader: {
        userId: new Guid(result.userId),
        legalEntityId: new Guid(result.legalEntityId),
      },
      navigate,
      session,
    });
  }

  async function publishProfile(individual: Individual) {
    if (!individual.id) return;
    try {
      setIsPublishing(true);
      await dispatch(
        individualsApi.endpoints.updateIndividual.initiate({
          individualId: individual.id.value,
          isVisible: true,
        }),
      ).unwrap();
      setIsPublished(true);
      enqueueSnackbar("Profile published", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't publish profile. Please try again", { variant: "error" });
    }
    setIsPublishing(false);
  }

  function getCompanyDetail(): React.ReactNode {
    if (!result.companyName) return null;
    if (!result.companyId) return getText(["companyName"], result);
    return (
      <MarketplaceProfileLink
        profileType={MarketplaceResultType.Company}
        profileId={result.companyId}
        onOpen={(event) => openCompanyProfile(event, result.companyId)}
        underline="hover"
      >
        {getText(["companyName"], result)}
      </MarketplaceProfileLink>
    );
  }

  function listFact(
    label: string,
    items: readonly (string | null | undefined)[] | null | undefined,
  ): MarketplaceResultFact | null {
    return items?.some((item) => item?.trim()) ? { label, value: <MarketplaceResultFactList items={items} /> } : null;
  }

  function textFact(label: string, text: string | null | undefined): MarketplaceResultFact | null {
    return text?.trim() ? { label, value: text.trim() } : null;
  }

  // What someone choosing counsel reads first comes first: a compact card keeps only the first two.
  function getFacts(): MarketplaceResultFact[] {
    const rate = result.billingRate ? getBillingRateDisplayValue(result.billingRate) : "";
    const availability = [result.isOnCall && "On call", result.collaborates && "Collaborates with others"];
    return [
      listFact("Practice areas", result.practiceAreas),
      listFact("Technical background", result.technicalBackgrounds),
      listFact("Registered in", result.regionsRegistered),
      textFact("Registration no.", result.professionalRegistration),
      listFact("Licensed in", result.regionsLicensed),
      textFact("Bar admission", result.barAdmissionYear ? getText(["barAdmissionYear"], result) : ""),
      textFact("Billing rate", rate && result.billingUnit ? `${rate} / ${result.billingUnit}` : rate),
      textFact(
        "Applications",
        getApplicationsText(result.numberOfApplicationsDraftedProsecuted, result.numberOfAllowedApplications),
      ),
      textFact("Education", [result.degree?.trim(), result.school?.trim()].filter(Boolean).join(", ")),
      listFact("Languages", result.fluentLanguages),
      textFact("Insured", getWholeDollarText(result.insuredAmount)),
      listFact("Availability", availability.filter((item): item is string => Boolean(item))),
    ].filter((fact): fact is MarketplaceResultFact => fact !== null);
  }

  // Nothing here can be acted on: an unpublished individual cannot join a vendor-team, and only
  // they can publish themselves. Outside the picker the card still renders - reading the profile
  // is what the entity-wide member-information grant is for.
  if (embedded && isUnpublished && !isOwnProfile) return null;

  return (
    <React.Fragment>
      <MarketplaceResultCard
        resultType={MarketplaceResultType.Individual}
        label={fullName}
        compact={compactLayout}
        avatar={linkAvatarToProfile(
          <MarketplaceResultAvatar
            resultType={MarketplaceResultType.Individual}
            name={fullName}
            src={avatar}
            compact={compactLayout}
          />,
        )}
        name={
          <MarketplaceProfileLink
            profileType={MarketplaceResultType.Individual}
            profileId={individual.id.value}
            onOpen={(event) => openIndividualProfile(event, individual.id.value)}
            color="inherit"
            underline="always"
          >
            {fullName}
          </MarketplaceProfileLink>
        }
        // In a picker the card says who someone is and offers the button: avatar, name, company (#6040).
        // A bio, credentials, location and facts are for choosing whom to hire, which the marketplace
        // page is for; in a team picker a long bio made one card the height of the screen.
        details={
          embedded
            ? [getCompanyDetail()]
            : [result.credentials?.trim(), getCompanyDetail(), getCityAndStateText(result.location)]
        }
        headerAction={!embedded && <IndividualBookmarkButton individualId={individual.id} name={fullName} />}
        description={embedded ? undefined : result.additionalInfo}
        facts={embedded ? undefined : getFacts()}
        picker={embedded ? getAddButton(result) : undefined}
        footer={
          !embedded && (
            <MarketplaceResultFooter
              resultName={fullName}
              profilePath={getMarketplaceProfilePath(MarketplaceResultType.Individual, individual.id.value)}
              networkUserId={individual.userId}
              onCreateProposal={canCreateProposalFromResult() ? startProposal : undefined}
              onSendMessage={contactIndividual}
            />
          )
        }
      />
      {showChat && (
        <MarketplaceChatCardContent>
          <ChatWindow fillAvailableSpace forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
        </MarketplaceChatCardContent>
      )}
    </React.Fragment>
  );
}
